import { kurseApi, type KursItem } from '../../../lib/courses';
import { createTrial, type CreateTrialPayload } from '../../../lib/trials';
import { trialRequestsApi } from '../../../lib/trialRequests';
import type { TeacherTimetableSelectEvent } from '../../schedule/TeacherTimetable';
import type { TrialParticipantDraft } from './types';

type HandleCourseEventArgs = Readonly<{
  event: TeacherTimetableSelectEvent;
  addMinutes: (hhmm: string, minutes: number) => string;
}>;

export const resolveCoursePickFromEventAction = async ({
  event,
  addMinutes,
}: HandleCourseEventArgs): Promise<{ coursePick: { courseId: number; date: string; dayOfWeek: number; startTime: string; endTime: string; course?: KursItem | null }; hadLoadError: boolean } | null> => {
  if (event.type !== 'pattern') return null;
  const courseId = event.courseId ?? (() => {
    const matcher = /^pattern-(\d+)-/;
    const result = matcher.exec(String(event.id || ''));
    return result ? Number(result[1]) : undefined;
  })();
  if (!courseId) return null;

  const ymd = String(event.date || '').slice(0, 10);
  const hhmm = String(event.startTime || '').slice(0, 5);
  if (!ymd || !hhmm) return null;
  const dayOfWeek = Number(event.dayOfWeek || 0);
  const endHHMM = String(event.endTime || addMinutes(hhmm, 30)).slice(0, 5);
  const fallbackDow = dayOfWeek || (((new Date(`${ymd}T00:00:00`).getDay() + 6) % 7) + 1);

  try {
    const course = await kurseApi.get(courseId);
    return {
      hadLoadError: false,
      coursePick: { courseId, course, date: ymd, dayOfWeek: fallbackDow, startTime: hhmm, endTime: endHHMM },
    };
  } catch {
    return {
      hadLoadError: true,
      coursePick: { courseId, course: null, date: ymd, dayOfWeek: fallbackDow, startTime: hhmm, endTime: endHHMM },
    };
  }
};

type SubmitArgs = Readonly<{
  fachId?: number;
  teacherId?: number;
  date: string;
  startTime: string;
  endTime: string;
  roomId?: number;
  sendEmails: boolean;
  skipStudentEmail: boolean;
  skipTeacherEmail: boolean;
  sendSms: boolean;
  skipStudentSms: boolean;
  skipTeacherSms: boolean;
  trialRequestId?: number;
  /** Die Angaben des Kunden — sie landen in dessen Profil, nicht im des Lernenden. */
  trialParticipantDraft: TrialParticipantDraft;
  /** Der Teilnehmer steht schon fest: Kunde und Profil sind vorher gesichert. */
  subprofileId: string;
  teilnehmerName: string;
  selectedCourseId?: number;
  selectedStandortId?: number;
  createHistoryText: string;
  historyTemplateKey?: string;
  /** Der Text aus dem Kommunikationsschritt, je Gruppe („student", „teacher"). */
  mailEntwuerfe?: Record<string, { subject: string; html: string; text: string; sms: string }>;
}>;

export const submitTrialWizardAction = async ({
  fachId,
  teacherId,
  date,
  startTime,
  endTime,
  roomId,
  sendEmails,
  skipStudentEmail,
  skipTeacherEmail,
  sendSms,
  skipStudentSms,
  skipTeacherSms,
  trialRequestId,
  trialParticipantDraft,
  subprofileId,
  teilnehmerName,
  selectedCourseId,
  selectedStandortId,
  createHistoryText,
  historyTemplateKey,
  mailEntwuerfe,
}: SubmitArgs): Promise<{ created: unknown; alreadyConverted?: boolean } | null> => {
  if (!fachId || !teacherId || !date || !startTime || !subprofileId) return null;

  if (trialRequestId) {
    const isCompany = trialParticipantDraft.isCompany;
    const effectiveFirstName = trialParticipantDraft.firstName.trim();
    const effectiveLastName = trialParticipantDraft.lastName.trim();
    const formattedAddress = [
      trialParticipantDraft.street.trim(),
      [trialParticipantDraft.postalCode.trim(), trialParticipantDraft.city.trim()].filter(Boolean).join(' '),
      trialParticipantDraft.country.trim(),
    ]
      .filter(Boolean)
      .join(', ');
    const conversion = await trialRequestsApi.convert(trialRequestId, {
      fachId,
      teacherId,
      date,
      startTime,
      endTime,
      roomId: roomId ?? null,
      sendEmails,
      skipStudentEmail,
      skipTeacherEmail,
      sendSms,
      skipStudentSms,
      skipTeacherSms,
      historyTemplateKey,
      drafts: mailEntwuerfe,
      linkedSubprofileId: subprofileId,
      participant: {
        isCompany,
        salutation: trialParticipantDraft.salutation || null,
        companyName: trialParticipantDraft.companyName.trim() || null,
        firstName: effectiveFirstName,
        lastName: effectiveLastName,
        birthDate: trialParticipantDraft.birthDate || null,
        structuredAddress:
          trialParticipantDraft.street.trim() ||
          trialParticipantDraft.postalCode.trim() ||
          trialParticipantDraft.city.trim() ||
          trialParticipantDraft.country.trim()
            ? {
                street: trialParticipantDraft.street.trim() || undefined,
                postalCode: trialParticipantDraft.postalCode.trim() || undefined,
                zip: trialParticipantDraft.postalCode.trim() || undefined,
                city: trialParticipantDraft.city.trim() || undefined,
                country: trialParticipantDraft.country.trim() || undefined,
              }
            : undefined,
        address: formattedAddress || null,
        email: trialParticipantDraft.email,
        phone: trialParticipantDraft.phone || null,
      },
    });
    const conversionRecord = conversion as Record<string, unknown>;
    const created =
      conversionRecord.created && typeof conversionRecord.created === 'object'
        ? conversionRecord.created
        : conversion;
    return { created, alreadyConverted: conversionRecord.alreadyConverted === true };
  }

  const payload: CreateTrialPayload = {
    studentName: teilnehmerName,
    date,
    startTime,
    endTime,
    subprofileId,
    teacherId,
    fachId,
    roomId: roomId ?? null,
    sendEmails,
    skipStudentEmail,
    skipTeacherEmail,
    sendSms,
    skipStudentSms,
    skipTeacherSms,
    courseId: selectedCourseId ?? undefined,
    standort_id: selectedStandortId ?? undefined,
    historyTemplateKey: historyTemplateKey || undefined,
    drafts: mailEntwuerfe,
    history: createHistoryText ? { type: 'trial', description: createHistoryText } : undefined,
  };
  const created = await createTrial(payload);
  return { created };
};
